import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DemoService } from '../../../../demo.service';
import { StaffAchievementReport, StaffAchievementSearch, StaffAchievementSnapshot } from '../../../../shared/models/staff-achievement.model';
import { achievementRequest } from './achievement-presentation';
import { benchmarkIsActive, STAFF_ACTIVE_BENCHMARKS_UPDATE_MESSAGE } from '../../../../shared/models/benchmark-policy.model';
import { staffExclusionsMatch, STAFF_EXCLUSIONS_UPDATE_MESSAGE } from '../../../../shared/models/staff-exclusions.model';
import { staffCalendarMatches, STAFF_CALENDAR_UPDATE_MESSAGE } from '../../../../shared/models/staff-reporting-calendar.model';

@Component({
  selector: 'app-staff-achievement-live',
  template: `<section class="staff-achievement-live">
    <div class="achievement-filters">
      <h3>Staff Work</h3>
      <p>Completed task counts and Weighted Work Minutes for the shared reporting dates and staff selection.</p>
      <label><input type="checkbox" [(ngModel)]="allActions" (ngModelChange)="reload()"> Include all actions in Staff Work</label>
      <p class="text-muted">Applies to the staff work summary and detail below, including timed and untimed actions. Uncheck to select specific actions.</p>
      <ejs-multiselect *ngIf="!allActions" [dataSource]="availableActions" [fields]="actionFields" [value]="selectedActions"
        placeholder="Select timed or untimed actions" [mode]="'Box'" [allowFiltering]="true" (change)="selectActions($event.value)"></ejs-multiselect>
      <button type="button" class="btn btn-default btn-sm" (click)="reload()">Refresh staff work</button>
    </div>
    <app-staff-achievement [report]="report" [loading]="loading" [error]="error"></app-staff-achievement>
  </section>`,
  styles: ['.achievement-filters { padding: 16px; background: #f5f8fc; border: 1px solid #d8e2ee; margin-bottom: 12px; } .achievement-filters h3 { margin-top: 0; } button { margin-left: 12px; }']
})
export class StaffAchievementLiveComponent implements OnChanges, OnDestroy {
  @Input() search: StaffAchievementSearch | null = null;
  report: StaffAchievementReport | null = null;
  loading = false;
  error = '';
  allActions = true;
  selectedActions: number[] = [];
  availableActions: Array<{ log_type_id: number; name: string }> = [];
  actionFields = { value: 'log_type_id', text: 'name' };
  private request: Subscription;
  private generation = 0;
  private requestKey = '';
  constructor(private api: DemoService) {}
  ngOnChanges(): void { this.reload(); }
  ngOnDestroy(): void { this.generation++; this.request?.unsubscribe(); }
  selectActions(ids: Array<number | string> | null): void { this.selectedActions = (ids || []).map(Number); this.reload(); }
  reload(): void {
    const generation = ++this.generation;
    this.request?.unsubscribe(); this.report = null; this.error = ''; this.loading = false;
    if (!this.search?.when_from || !this.search?.when_to) { return; }
    const filters = achievementRequest(this.search, this.allActions ? null : this.selectedActions);
    this.requestKey = JSON.stringify(filters); this.loading = true;
    this.request = this.api.fetchStaffAchievements(filters).subscribe({
      next: report => {
        if (generation !== this.generation) { return; }
        if (!staffCalendarMatches(report.context, filters)) { this.error = STAFF_CALENDAR_UPDATE_MESSAGE; this.loading = false; return; }
        if (!staffExclusionsMatch(report?.context, filters)) { this.error = STAFF_EXCLUSIONS_UPDATE_MESSAGE; this.loading = false; return; }
        if (report && report.context?.benchmark_policy !== 'active_only') { this.error = STAFF_ACTIVE_BENCHMARKS_UPDATE_MESSAGE; this.loading = false; return; }
        this.report = report; this.availableActions = report.available_actions; this.loading = false;
      },
      error: failure => {
        if (generation !== this.generation) { return; }
        this.error = failure.error?.error?.message || 'Staff achievement is unavailable. Refresh to retry.'; this.loading = false;
      }
    });
  }
  snapshot(currentSearch: StaffAchievementSearch): StaffAchievementSnapshot {
    const filters = achievementRequest(currentSearch, this.allActions ? null : this.selectedActions);
    if (this.report && !this.loading && !this.error && JSON.stringify(filters) === this.requestKey) { return JSON.parse(JSON.stringify(this.report)); }
    return { version: 1, status: 'unavailable', reason: this.error || 'Staff achievement was not ready for these filters when saved.' };
  }
}
